import { gql, useMutation, useQuery } from '@apollo/client'
import { useEffect } from 'react'

import {
  BlockUser,
  BlockUserMutation,
  BlockUserMutationVariables,
  CommentBoxFragmentDoc,
  CommentsConnectionFragmentDoc,
  CreateItemCommentMutation,
  CreateItemCommentMutationVariables,
  DeleteItemCommentMutation,
  DeleteItemCommentMutationVariables,
  ItemCommentQuery,
  ItemCommentQueryVariables,
  LikeItemCommentMutation,
  LikeItemCommentMutationVariables,
  UnlikeItemCommentMutation,
  UnlikeItemCommentMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import {
  useCurrentUser,
  useLoginModal,
  useReplyToComment,
  useRouter,
} from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'
import { openReportForm } from '@/core/googleForm'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router } = useRouter<{ itemId: string }>()
  const { analytics } = useAnalytics()
  const { user } = useCurrentUser()
  const { showModal } = useLoginModal()
  const {
    data: { replyToDisplayName, replyToAccountId, replyToCommentId },
    onReplyToReply,
    onRemoveReplyToReply,
  } = useReplyToComment()

  const { data, loading, fetchMore, refetch } = useQuery<
    ItemCommentQuery,
    ItemCommentQueryVariables
  >(QUERY, {
    variables: { itemId: router.query.itemId },
  })

  const [createItemComment] = useMutation<
    CreateItemCommentMutation,
    CreateItemCommentMutationVariables
  >(CREATE_ITEM_COMMENT)

  const [deleteItemComment] = useMutation<
    DeleteItemCommentMutation,
    DeleteItemCommentMutationVariables
  >(DELETE_ITEM_COMMENT)

  const [likeItemComment] = useMutation<
    LikeItemCommentMutation,
    LikeItemCommentMutationVariables
  >(LIKE_ITEM_COMMENT)

  const [unlikeItemComment] = useMutation<
    UnlikeItemCommentMutation,
    UnlikeItemCommentMutationVariables
  >(UNLIKE_ITEM_COMMENT)

  const [blockUser] = useMutation<
    BlockUserMutation,
    BlockUserMutationVariables
  >(BlockUser)

  useEffect(() => {
    if (!user) {
      showModal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  return {
    state: {
      loading,
      artistId: data?.item?.issuedArtist?.id ?? '',
      hasNextPage: !!data?.item?.comments?.pageInfo.hasNextPage,
      comments: data?.item?.comments ?? null,
      user: data?.user ?? null,
      itemId: router.query.itemId,
      replyToDisplayName,
    },
    actions: {
      async onDeleteComment(commentId) {
        await deleteItemComment({
          variables: { id: router.query.itemId, commentId },
        }).then(() => {
          analytics?.track('click_deleteComment', {
            applicationKey: config.applicationKey,
            itemId: router.query.itemId,
            artistId: data?.item?.issuedArtist?.id,
            name: 'itemDetail',
          })
        })
      },
      async onLikeOrUnlikeComment({ commentId, isLike, id, likeCount }) {
        if (isLike) {
          await unlikeItemComment({
            variables: {
              inputs: { commentId, id: id ?? router.query.itemId },
            },
            optimisticResponse() {
              return {
                unlikeItemComment: {
                  __typename: 'Comment',
                  id: commentId,
                  isLike: false,
                  likeCount: --likeCount,
                },
              }
            },
          }).then(() => {
            analytics?.track('click_unlikeComment', {
              applicationKey: config.applicationKey,
              itemId: router.query.itemId,
              artistId: data?.item?.issuedArtist?.id,
              name: 'itemDetail',
            })
          })
          return
        }
        await likeItemComment({
          variables: {
            inputs: { commentId, id: id ?? router.query.itemId },
          },
          optimisticResponse() {
            return {
              likeItemComment: {
                __typename: 'Comment',
                id: commentId,
                isLike: true,
                likeCount: ++likeCount,
              },
            }
          },
        }).then(() => {
          analytics?.track('click_likeComment', {
            applicationKey: config.applicationKey,
            itemId: router.query.itemId,
            artistId: data?.item?.issuedArtist?.id,
            name: 'itemDetail',
          })
        })
      },
      onReplyToReply,
      onRemoveReplyToReply,
      async onFetchMore() {
        await fetchMore({
          variables: {
            itemId: router.query.itemId,
            pageInput: { cursor: data?.item?.comments?.pageInfo.endCursor },
          },
        })
      },
      async onReportUser(contentId, userId, accountId) {
        await openReportForm({
          email: data?.user?.email ?? '',
          contentType: 'item-comments',
          contentId,
          userId,
          accountId,
        })
      },
      async onBlockUser(userId) {
        await blockUser({ variables: { id: userId } })
        await refetch({ itemId: router.query.itemId })
      },
      async onCreateItemComment(inputs, artistId) {
        await createItemComment({
          variables: {
            id: router.query.itemId,
            inputs: {
              artistId,
              replyToAccountId,
              replyToId: replyToCommentId,
              ...inputs,
            },
          },
        }).then(() => {
          analytics?.track('click_sendComment', {
            applicationKey: config.applicationKey,
            itemId: router.query.itemId,
            artistId: data?.item?.issuedArtist?.id,
            name: 'itemDetail',
          })
        })
        onRemoveReplyToReply()
      },
    },
  }
})

const QUERY = gql`
  query ItemComment($itemId: ID!, $pageInput: PageInput) {
    item(id: $itemId) {
      id
      issuedArtist {
        id
      }
      commentCount
      comments(pageInput: $pageInput) {
        ...CommentsConnection
      }
    }
    user {
      id
      email
      displayName
      accountId
      formattedAccountId @client
      avatar {
        uri
        smallUri
      }
    }
  }
  ${CommentsConnectionFragmentDoc}
`

const CREATE_ITEM_COMMENT = gql`
  mutation CreateItemComment($id: String!, $inputs: CommunityCommentInput!) {
    createItemComment(id: $id, inputs: $inputs) {
      ...CommentBox
    }
  }
  ${CommentBoxFragmentDoc}
`

const DELETE_ITEM_COMMENT = gql`
  mutation DeleteItemComment($id: String!, $commentId: String!) {
    deleteItemComment(id: $id, commentId: $commentId) {
      ...CommentBox
    }
  }
  ${CommentBoxFragmentDoc}
`

const LIKE_ITEM_COMMENT = gql`
  mutation LikeItemComment($inputs: CommunityCommentLikeUnlikeInput!) {
    likeItemComment(inputs: $inputs) {
      id
      isLike
      likeCount
    }
  }
`

const UNLIKE_ITEM_COMMENT = gql`
  mutation UnlikeItemComment($inputs: CommunityCommentLikeUnlikeInput!) {
    unlikeItemComment(inputs: $inputs) {
      id
      isLike
      likeCount
    }
  }
`
